<script setup lang="ts">
import { computed, nextTick, ref, useId, useTemplateRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useEventListener } from '@vueuse/core'
import Dialog from 'primevue/dialog'
import ProgressSpinner from 'primevue/progressspinner'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import LimitAccessAlert from '@/components/LimitAccessAlert.vue'
import { pathwaysUrls } from '@/util/links'
import { useVisibilityStore } from '@/visibility/visibility.store'
import ItemHeading from './ItemHeading.vue'
import ItemView from './ItemView.vue'
import ObjectiveHeading from './ObjectiveHeading.vue'
import { useAssessmentItemsStore } from './assessment_items.store'
import type { ContentAccessInfo } from './content_access_info'
import { DEFAULT_BUTTON_CLASSES, levelActionTextClass, levelButtonClasses, type ObjectiveLevel } from './objective_levels'

const FETCH_ERROR_MESSAGE = 'There was a problem fetching this item. Please try again.'

const props = withDefaults(
  defineProps<{
    itemId: string
    level: ObjectiveLevel
    title?: string
    isOpen?: boolean
    accessInfo: ContentAccessInfo
  }>(),
  { title: 'Sample Assessment Item', isOpen: false }
)

const emit = defineEmits<{
  'update:isOpen': [isOpen: boolean]
  print: []
}>()

const { editorVisibility } = storeToRefs(useVisibilityStore())
const itemsStore = useAssessmentItemsStore()

const titleId = useId()
const dialogContent = useTemplateRef<HTMLElement>('dialogContent')
const isFetchingItem = ref(false)
const errorMessage = ref('')
let latestLoadId = 0

const item = computed(() => itemsStore.itemById(props.itemId))
const hasPassages = computed(() => (item.value?.passages.length ?? 0) > 0)
const editItemUrl = computed(() => pathwaysUrls.editItem(props.itemId))
const printButtonClasses = computed(() => levelButtonClasses(props.level))

// Wider when passages sit beside the prompt, as the page has always done.
const dialogWidthClasses = computed(() => {
  if (hasPassages.value) {
    return 'md:max-w-xl lg:max-w-4xl xl:max-w-6xl'
  }
  return 'md:max-w-xl lg:max-w-4xl'
})

const dialogPassThrough = computed(() => ({
  mask: { class: '!z-[99999] overflow-y-auto bg-black/50 px-2.5' },
  root: {
    class: [
      'relative mb-48 mt-2.5 max-h-none w-full shrink-0 rounded-sm border border-black/20 bg-white bg-clip-padding shadow-lg md:mx-auto md:mt-8',
      dialogWidthClasses.value
    ]
  }
}))

// Only the newest load may update the modal; an earlier one that settles late is ignored.
async function loadItem() {
  const loadId = ++latestLoadId
  isFetchingItem.value = true
  errorMessage.value = ''
  try {
    await itemsStore.fetchItem(props.itemId)
  } catch (error) {
    console.error(error)
    if (loadId === latestLoadId) {
      errorMessage.value = FETCH_ERROR_MESSAGE
    }
  } finally {
    if (loadId === latestLoadId) {
      isFetchingItem.value = false
    }
  }
}

watch(
  [() => props.isOpen, () => props.itemId],
  ([isOpen]) => {
    if (isOpen) {
      loadItem()
    }
  },
  { immediate: true }
)

function close() {
  emit('update:isOpen', false)
}

// PrimeVue focuses nothing inside a container-slot dialog, which would leave Escape unheard until the
// user tabbed in. It reads the trigger as the element to refocus on close right after emitting show,
// so focus moves in on the next tick.
async function focusDialog() {
  await nextTick()
  dialogContent.value?.focus()
}

// Focus can still fall to the page behind the dialog (a click on its text, say), and Escape should close
// the item from there as Vue 2 does. Escape inside a resource preview opened from the item is not on the body.
useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  if (props.isOpen && event.key === 'Escape' && event.target === document.body) {
    close()
  }
})

function updateVisible(isVisible: boolean) {
  if (!isVisible) {
    close()
  }
}
</script>

<template>
  <!-- Escape is handled on the dialog itself rather than by PrimeVue's document listener, so
       Escape in a resource preview opened from the item closes only that preview. -->
  <Dialog
    :visible="isOpen"
    modal
    position="top"
    :draggable="false"
    :close-on-escape="false"
    dismissable-mask
    block-scroll
    :aria-labelledby="titleId"
    :pt="dialogPassThrough"
    @update:visible="updateVisible"
    @show="focusDialog"
  >
    <template #container>
      <div ref="dialogContent" tabindex="-1" class="outline-none" data-testid="item-modal" @keydown.esc="close">
        <div class="absolute left-0 top-0 w-full">
          <ObjectiveHeading :level="level" :title="title" :title-id="titleId">
            <template #actions>
              <button type="button" :class="printButtonClasses" data-testid="item-modal-print-top" @click="emit('print')">
                <span class="material-icons-round text-text-lg" aria-hidden="true">print</span>
                Print
              </button>
              <button
                type="button"
                class="relative top-1 ml-2 align-middle opacity-65 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gray-900"
                :class="levelActionTextClass(level)"
                aria-label="Close"
                data-testid="item-modal-close-top"
                @click="close"
              >
                <span class="material-icons-round text-display-sm" aria-hidden="true">close</span>
              </button>
            </template>
          </ObjectiveHeading>
        </div>

        <div class="px-4 pb-4 pt-20">
          <CopyTrackWrapper type="objective" :level="level" gate-copy class="text-text-xl font-regular leading-6">
            <slot name="objective" />
          </CopyTrackWrapper>

          <LimitAccessAlert v-if="accessInfo.blur" :access-info="accessInfo" class="mt-4 md:mx-auto md:w-10/12" />

          <ItemHeading :level="level">Student Task and Resources</ItemHeading>

          <a
            v-if="editorVisibility"
            :href="editItemUrl"
            target="_blank"
            class="float-right inline-flex items-center gap-1 rounded-xs border border-gray-400 bg-white px-1 py-px text-text-sm leading-normal text-gray-900 hover:bg-gray-300"
            data-testid="item-modal-edit"
          >
            <span class="material-icons-round text-text-sm" aria-hidden="true">edit</span>
            Edit
          </a>
          <div v-if="isFetchingItem" class="mb-2.5 flex items-center justify-center gap-2 text-gray-700" data-testid="item-modal-fetching">
            <ProgressSpinner stroke-width="8" class="!m-0 h-4 w-4 stroke-gray-700" aria-hidden="true" />
            Fetching item...
          </div>
          <p
            v-if="errorMessage"
            role="alert"
            class="mb-5 rounded-xs border border-red-100 bg-red-100 p-4 text-red-300"
          >
            {{ errorMessage }}
          </p>

          <CopyTrackWrapper v-if="item" type="item" :title="title" :uuid="item.uuid" gate-copy :demo="accessInfo.demo">
            <ItemView :item="item" :level="level" :is-locked="accessInfo.blur" />
          </CopyTrackWrapper>
        </div>

        <div class="flex justify-end gap-1 border-t border-gray-300 p-4">
          <button type="button" :class="printButtonClasses" data-testid="item-modal-print-bottom" @click="emit('print')">
            <span class="material-icons-round text-text-lg" aria-hidden="true">print</span>
            Print
          </button>
          <button type="button" :class="DEFAULT_BUTTON_CLASSES" data-testid="item-modal-close-bottom" @click="close">Close</button>
        </div>
      </div>
    </template>
  </Dialog>
</template>
